/**
 * TanStack Query hooks for multi-profile management
 */

import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import type { CompanyProfile, CompanyProfileUpdate } from '@/types/companyProfile';
import {
  getAllProfiles,
  getMyCompanyProfile,
  getProfileById,
  activateProfile,
  createCompanyProfile,
  updateCompanyProfile,
  deleteCompanyProfile,
} from '@/lib/api/companyProfile';
import { useProfileStore } from '@/stores/profileStore';
import { useAuthStore } from '@/stores/authStore';

// Query keys
export const profileKeys = {
  all: ['profiles'] as const,
  lists: () => [...profileKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...profileKeys.lists(), filters] as const,
  details: () => [...profileKeys.all, 'detail'] as const,
  detail: (id: string) => [...profileKeys.details(), id] as const,
  active: () => [...profileKeys.all, 'active'] as const,
};

/**
 * Hook to fetch all profiles for the current user
 */
export function useProfiles() {
  const { setProfiles, setLoadingProfiles } = useProfileStore();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: profileKeys.lists(),
    queryFn: async () => {
      setLoadingProfiles(true);
      try {
        const profiles = await getAllProfiles();
        setProfiles(profiles);
        return profiles;
      } finally {
        setLoadingProfiles(false);
      }
    },
    enabled: isAuthenticated, // Only fetch profiles when authenticated
    staleTime: 1000 * 60 * 10, // Consider data fresh for 10 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });
}

/**
 * Hook to fetch the active profile
 */
export function useActiveProfile(options?: Omit<UseQueryOptions<CompanyProfile>, 'queryKey' | 'queryFn'>) {
  const { setActiveProfile, setLoadingActive } = useProfileStore();

  return useQuery({
    queryKey: profileKeys.active(),
    queryFn: async () => {
      setLoadingActive(true);
      try {
        const profile = await getMyCompanyProfile();
        setActiveProfile(profile);
        return profile;
      } finally {
        setLoadingActive(false);
      }
    },
    staleTime: 1000 * 60 * 10, // Consider data fresh for 10 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    retry: 1, // Only retry once if active profile fetch fails
    ...options, // Allow overriding with custom options (e.g., enabled: false)
  });
}

/**
 * Hook to fetch a specific profile by ID
 */
export function useProfile(profileId: string | undefined) {
  return useQuery({
    queryKey: profileKeys.detail(profileId || ''),
    queryFn: () => getProfileById(profileId!),
    enabled: !!profileId, // Only run query if profileId is provided
    staleTime: 1000 * 60 * 10, // Consider data fresh for 10 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });
}

/**
 * Hook to switch/activate a profile with optimistic updates
 */
export function useProfileSwitch() {
  const queryClient = useQueryClient();
  const { setActiveProfile } = useProfileStore();

  return useMutation({
    mutationFn: async (profileId: string) => {
      const activatedProfile = await activateProfile(profileId);
      return activatedProfile;
    },
    // Optimistic update for instant UI feedback
    onMutate: async (profileId) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: profileKeys.lists() });
      await queryClient.cancelQueries({ queryKey: profileKeys.active() });

      // Snapshot the previous values
      const previousProfiles = queryClient.getQueryData<CompanyProfile[]>(profileKeys.lists());
      const previousActive = queryClient.getQueryData<CompanyProfile>(profileKeys.active());

      // Optimistically update profiles list
      if (previousProfiles) {
        const optimisticProfiles = previousProfiles.map((p) => ({
          ...p,
          is_active: p.id === profileId,
        }));
        queryClient.setQueryData(profileKeys.lists(), optimisticProfiles);

        // Optimistically update active profile
        const newActive = optimisticProfiles.find((p) => p.id === profileId);
        if (newActive) {
          queryClient.setQueryData(profileKeys.active(), newActive);
          setActiveProfile(newActive);
        }
      }

      // Return context with previous values for potential rollback
      return { previousProfiles, previousActive };
    },
    onSuccess: (activatedProfile) => {
      // Update the active profile in the store with server data
      setActiveProfile(activatedProfile);

      // Update cache with server response
      const profiles = queryClient.getQueryData<CompanyProfile[]>(profileKeys.lists());
      if (profiles) {
        const updatedProfiles = profiles.map((p) => ({
          ...p,
          is_active: p.id === activatedProfile.id,
        }));
        queryClient.setQueryData(profileKeys.lists(), updatedProfiles);
      }

      queryClient.setQueryData(profileKeys.active(), activatedProfile);

      toast.success(`Switched to "${activatedProfile.profile_name}"`);
    },
    onError: (error: Error, _profileId, context) => {
      // Rollback to previous values on error
      if (context?.previousProfiles) {
        queryClient.setQueryData(profileKeys.lists(), context.previousProfiles);
      }
      if (context?.previousActive) {
        queryClient.setQueryData(profileKeys.active(), context.previousActive);
        setActiveProfile(context.previousActive);
      }

      toast.error(`Failed to switch profile: ${error.message}`);
    },
    // Always refetch after error or success to ensure consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
      queryClient.invalidateQueries({ queryKey: profileKeys.active() });
    },
  });
}

/**
 * Hook to create a new profile
 */
export function useCreateProfile() {
  const queryClient = useQueryClient();
  const { addProfile } = useProfileStore();

  return useMutation({
    mutationFn: createCompanyProfile,
    onSuccess: (newProfile) => {
      // Add the new profile to the store
      addProfile(newProfile);

      // Invalidate and refetch profiles list
      queryClient.invalidateQueries({ queryKey: profileKeys.lists() });

      // If this is the active profile, invalidate active profile query
      if (newProfile.is_active) {
        queryClient.invalidateQueries({ queryKey: profileKeys.active() });
      }

      toast.success(`Profile "${newProfile.profile_name}" created successfully`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to create profile: ${error.message}`);
    },
  });
}

/**
 * Hook to update a profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { updateProfile: updateProfileInStore } = useProfileStore();

  return useMutation({
    mutationFn: ({
      profileId,
      data,
    }: {
      profileId: string;
      data: CompanyProfileUpdate;
    }) => updateCompanyProfile(profileId, data),
    onSuccess: (updatedProfile) => {
      // Update profile in store
      updateProfileInStore(updatedProfile.id, updatedProfile);

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: profileKeys.detail(updatedProfile.id) });
      queryClient.invalidateQueries({ queryKey: profileKeys.lists() });

      if (updatedProfile.is_active) {
        queryClient.invalidateQueries({ queryKey: profileKeys.active() });
      }

      toast.success(`Profile "${updatedProfile.profile_name}" updated successfully`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update profile: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a profile
 */
export function useDeleteProfile() {
  const queryClient = useQueryClient();
  const { removeProfile } = useProfileStore();

  return useMutation({
    mutationFn: deleteCompanyProfile,
    onSuccess: (_, deletedProfileId) => {
      // Remove profile from store
      removeProfile(deletedProfileId);

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
      queryClient.invalidateQueries({ queryKey: profileKeys.active() });

      toast.success('Profile deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete profile: ${error.message}`);
    },
  });
}

/**
 * Hook to prefetch profile data
 * Useful for optimistic loading (e.g., on hover)
 */
export function usePrefetchProfile() {
  const queryClient = useQueryClient();

  return {
    // Prefetch a specific profile by ID
    prefetchProfile: (profileId: string) => {
      return queryClient.prefetchQuery({
        queryKey: profileKeys.detail(profileId),
        queryFn: () => getProfileById(profileId),
        staleTime: 1000 * 60 * 10,
      });
    },

    // Prefetch all profiles
    prefetchProfiles: () => {
      return queryClient.prefetchQuery({
        queryKey: profileKeys.lists(),
        queryFn: getAllProfiles,
        staleTime: 1000 * 60 * 10,
      });
    },

    // Prefetch active profile
    prefetchActiveProfile: () => {
      return queryClient.prefetchQuery({
        queryKey: profileKeys.active(),
        queryFn: getMyCompanyProfile,
        staleTime: 1000 * 60 * 10,
      });
    },
  };
}
